/* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { catchError, filter, finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { forkJoin, Observable, of, Subject } from 'rxjs'

import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { emissionClasses } from '@shared/mock-data/emission-classes'
import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { Depot } from '@shared/models/depot.model'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { Fuel, FuelTypeLabel, InputVehicle, Vehicle, VehicleModelType } from '@shared/models/vehicle.model'
import { DevicesService } from '@shared/modules/devices/devices.service'
import { Device } from '@shared/models/device.model'
import { Setting } from '@shared/models/setting.model'
import { PermissionsService } from '@shared/services/permissions.service'
import { FuelService } from '@app/modules/client/fuel/fuel.service'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { DialogService } from '@shared/dialogs/dialog.service'
import { TranslateService } from '@ngx-translate/core'
import { DeviceInstallUninstallDialogComponent } from '@shared/dialogs/device-install-uninstall-dialog/device-install-uninstall-dialog.component'
import dayjs from 'dayjs'
import { CheckDeviceStatusDialogComponent } from '@shared/dialogs/check-device-status-dialog/check-device-status-dialog.component'
import { LinValidators } from '@core/helpers/validators'

@Component({
  selector: 'lin-create-vehicle',
  templateUrl: './create-vehicle.component.html',
  standalone: false,
})
export class CreateVehicleComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  vehicleForm: FormGroup
  isSubmitting = false
  depots: Depot[]
  ecoSpeed: Setting
  fuel: Fuel[]
  excessiveIdling: Setting
  isLoaded = false
  isFormChanged: ReturnType<typeof takeFormSnapshot>
  vehicleTypesList: VehicleModelType[]

  private teamId: number
  private deviceId: string
  private device: Device
  private isAdmin: boolean

  readonly FuelTypeLabel = FuelTypeLabel
  readonly PermissionsSource = PermissionsSource
  readonly emissionClasses = emissionClasses

  private readonly onDestroy$ = new Subject<void>()

  get currentYear(): number {
    return dayjs().tz().year()
  }

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private vehiclesService: VehiclesService,
    private devicesService: DevicesService,
    private authService: AuthService,
    private unsavedFormTrackerService: UnsavedFormTracker,
    private route: ActivatedRoute,
    private permissionsService: PermissionsService,
    private fuelService: FuelService,
    private readonly dialog: DialogService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.unsavedFormTrackerService.track(this)

    this.teamId = this.route.snapshot.queryParams.teamId as number
    this.deviceId = this.route.snapshot.queryParams.deviceId as string

    this.deviceId &&
      this.devicesService
        .getDevice(this.deviceId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((device) => (this.device = device))

    this.fetchClientTeamId()
    this.initForm()
    this.getData()
  }

  private fetchClientTeamId(): void {
    this.authService.currentUser$
      .pipe(
        first(),
        tap((user) => (this.isAdmin = user.isAdmin())),
        filter((user) => user.isClient()),
      )
      .subscribe((user) => (this.teamId = user.team.id))
  }

  private getData(): void {
    const depots$ = this.vehiclesService.getDepotList({ teamId: this.teamId })
    const ecoSpeed$ = this.vehiclesService.getDefaultEcoSpeed().pipe(catchError(() => of(null)))
    const excessiveIdling$ = this.vehiclesService.getDefaultExcessiveIdling().pipe(catchError(() => of(null)))
    const fuel$ = this.fuelService.getFuel().pipe(catchError(() => of([])))
    const vehicleTypes$ = this.vehiclesService.getVehiclesTypes().pipe(catchError(() => of([])))

    forkJoin([depots$, ecoSpeed$, excessiveIdling$, fuel$, vehicleTypes$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([depots, ecoSpeed, excessiveIdling, fuel, vehicleTypes]) => {
        this.depots = depots
        this.ecoSpeed = ecoSpeed
        this.excessiveIdling = excessiveIdling
        this.fuel = fuel
        this.vehicleTypesList = vehicleTypes

        this.isLoaded = true
      })
  }

  hasPermission(permissionType: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  private initForm(): void {
    this.vehicleForm = this.formBuilder.group({
      type: ['', Validators.required],
      make: '',
      makeModel: '',
      vehicleTitle: ['', Validators.required],
      regNo: ['', Validators.required],
      depot: '',
      vin: '',
      regDate: [null, LinValidators.yearValidator()],
      enginePower: '',
      engineCapacity: '',
      fuelType: '',
      fuelTankCapacity: '',
      averageFuel: '',
      emissionClass: '',
      co2Emissions: '',
      grossWeight: '',
      ecoSpeed: '',
      excessiveIdling: '',
    })

    this.checkVehicleVinForUnique()
    this.isFormChanged = takeFormSnapshot(this.vehicleForm)
  }

  private checkVehicleVinForUnique(): void {
    this.vehicleForm.controls.vin.valueChanges
      .pipe(
        filter(Boolean),
        takeUntil(this.onDestroy$),
        switchMap((vin: string) => this.vehiclesService.checkVehicleVin(this.teamId, vin)),
      )
      .subscribe((isUniqueVin) => {
        const error = isUniqueVin ? null : { existingName: true }

        this.vehicleForm.controls.vin.setErrors(error)
      })
  }

  saveData(): void {
    this.isSubmitting = true
    const saveData: Partial<InputVehicle> = this.prepareDataForSave()

    this.vehiclesService
      .addVehicle(saveData)
      .pipe(
        switchMap((vehicle) => this.openInstallationDialog(vehicle)),
        switchMap((data: Device | null) => {
          if (!data) return of(null)
          const title = <string>this.translateService.instant('DIALOGS.CHECK_DEVICE_STATUS.TITLE')
          return this.dialog.open(CheckDeviceStatusDialogComponent, title, data.deviceInstallation.vehicleId).afterClosed()
        }),
        finalize(() => (this.isSubmitting = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: () => this.navigateAfterSave(),
        error: handleFormErrors(this.vehicleForm),
      })
  }

  private navigateAfterSave(): void {
    const clientUrl = this.deviceId ? `client/devices/${this.deviceId}/vehicle` : 'client/fleet'
    const adminUrl = `admin/devices/${this.deviceId}/vehicle`
    const url = this.isAdmin ? adminUrl : clientUrl

    this.isFormChanged = takeFormSnapshot(this.vehicleForm)
    void this.router.navigate([url])
  }

  openInstallationDialog(vehicle: Vehicle): Observable<any> {
    if (!this.isAdmin && !this.deviceId) return of(null)

    const title = <string>this.translateService.instant('ADMIN.DEVICES.INSTALL')
    return this.dialog.open(DeviceInstallUninstallDialogComponent, title, { device: this.device, vehicle, install: true }).afterClosed()
  }

  private prepareDataForSave(): Partial<InputVehicle> {
    const formData = this.vehicleForm.value

    const vehicle: Partial<InputVehicle> = {
      typeId: formData.type,
      make: formData.make,
      makeModel: formData.makeModel,
      defaultLabel: formData.vehicleTitle,
      regNo: formData.regNo,
      depotId: formData.depot || null,
      vin: formData.vin,
      regDate: formData.regDate && `${<number>formData.regDate}-01-02`,
      enginePower: formData.enginePower || null,
      engineCapacity: formData.engineCapacity || null,
      fuelType: formData.fuelType || null,
      fuelTankCapacity: formData.fuelTankCapacity || null,
      averageFuel: formData.averageFuel || null,
      emissionClass: formData.emissionClass,
      co2Emissions: formData.co2Emissions || null,
      grossWeight: formData.grossWeight || null,
      ecoSpeed: formData.ecoSpeed,
      excessiveIdling: formData.excessiveIdling,
      teamId: this.teamId,
    }

    return vehicle
  }

  displayVehicleType(type?: VehicleModelType): string | null {
    return (type && type.name) || null
  }

  handleVehicleTypeSelect(typeId: number): void {
    this.vehicleForm.get('type').setValue(typeId)
    this.vehicleForm.markAsDirty()
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
